import { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CustomDropdown from "../CustomDropdown";
import api from "../../services/axios";

const days = [
  { value: "ALL", label: "All Days" },
  { value: "today", label: "Today" },
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "30 Days" },
  { value: 180, label: "6 Months" },
  { value: 365, label: "12 Months" },
];

export default function UserCallStatusChart() {
  const [selectedDays, setSelectedDays] = useState("ALL");
  const [selectedAgent, setSelectedAgent] = useState("ALL"); // 👈 agent filter
  const [agentsData, setAgentsData] = useState([]);
  const [agentOptions, setAgentOptions] = useState([{ value: "ALL", label: "All Agents" }]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.post("/getAllCallLogs", { filter: selectedDays });
        const logs = res.data?.data || [];

        // Group by answered_agent
        const grouped = {};
        logs.forEach((log) => {
          const agent = log.answered_agent || log.answered_agent_name || "" || "Unknown";
          if (!grouped[agent]) {
            grouped[agent] = { answered: 0, missed: 0, rejected: 0 };
          }
          if (log.call_status === "answered") grouped[agent].answered++;
          if (log.call_status === "missed") grouped[agent].missed++;
          if (log.call_status === "rejected") grouped[agent].rejected++;
        });

        // Format per agent
        const formatted = Object.entries(grouped).map(([agent, counts]) => {
          const total = counts.answered + counts.missed + counts.rejected;
          const answeredPercent = total ? Math.round((counts.answered / total) * 100) : 0;

          return {
            agent,
            percent: answeredPercent,
            chartData: [
              { name: "Answered", y: counts.answered, color: "#4CAF50" },
              { name: "Missed", y: counts.missed, color: "#FF9800" },
              { name: "Rejected", y: counts.rejected, color: "#F44336" },
            ],
          };
        });

        setAgentsData(formatted);

        // Build agent dropdown options
        setAgentOptions([
           { value: "selected", label: "selected" },
          { value: "ALL", label: "All Agents" },
          ...formatted.map((a) => ({ value: a.agent, label: a.agent })),
        ]);
      } catch (err) {
        console.error("Error fetching call logs:", err);
      }
    };

    fetchData();
  }, [selectedDays]);

  const handleDaysChange = (val) => setSelectedDays(val);
  const handleAgentChange = (val) => setSelectedAgent(val);

  // Create chart options
const getChartOptions = (chartData, percent, title = "Answered") => ({
  chart: {
    type: "pie",
    backgroundColor: "transparent",
  },
  title: {
    text: `${title} <br><b>${percent}%</b>`,
    align: "center",
    verticalAlign: "middle",
    y: 40,
    style: { fontSize: "14px", color: "#000" },
  },
  plotOptions: {
    pie: {
      startAngle: -90,
      endAngle: 90,
      center: ["50%", "65%"],
      size: "100%",
      innerSize: "80%",
      borderRadius: 6,
      dataLabels: { enabled: false },
      showInLegend: true, // 👈 show in legend
    },
  },
  series: [
    {
      name: "Calls",
      data: [
        { name: "Answered", y: chartData.find((d) => d.name === "Answered")?.y || 0, color: "#4CAF50" },
        { name: "Missed", y: chartData.find((d) => d.name === "Missed")?.y || 0, color: "#FF9800" },
        { name: "Rejected", y: chartData.find((d) => d.name === "Rejected")?.y || 0, color: "#F44336" },
      ],
    },
  ],
  legend: {
    align: "center",
    verticalAlign: "bottom",
    layout: "horizontal",
    reversed: false,   // 👈 ensures natural order
  },
  credits: { enabled: false },
});


  // 🔑 Handle filter
  let filteredAgents = [];

  if (selectedAgent === "ALL") {
    // Combine totals
    const totalCounts = agentsData.reduce(
      (acc, curr) => {
        curr.chartData.forEach((item) => {
          if (item.name === "Answered") acc.answered += item.y;
          if (item.name === "Missed") acc.missed += item.y;
          if (item.name === "Rejected") acc.rejected += item.y;
        });
        return acc;
      },
      { answered: 0, missed: 0, rejected: 0 }
    );

    const total = totalCounts.answered + totalCounts.missed + totalCounts.rejected;
    const answeredPercent = total ? Math.round((totalCounts.answered / total) * 100) : 0;

    filteredAgents = [
      {
        agent: "All Agents",
        percent: answeredPercent,
        chartData: [
          { name: "Answered", y: totalCounts.answered, color: "#94E9B8" },
          { name: "Missed", y: totalCounts.missed, color: "#FFDB56" },
          { name: "Rejected", y: totalCounts.rejected, color: "#FF0000" },
        ],
      },
    ];
  } else {
    filteredAgents = agentsData.filter((a) => a.agent === selectedAgent);
  }

  return (
    <div className="assisvia">
      <div className="assisvia-header d-flex justify-content-between">
        <h2 className="heatmap-title">User Call Status</h2>
        <div className="d-flex gap-2 w-50">
          <CustomDropdown
            options={days}
            placeholder="Select Days"
            onChange={handleDaysChange}
            name="days"
            className="selectinput"
            value={selectedDays}
          />
          <CustomDropdown
            options={agentOptions}
            placeholder="Select Agent"
            onChange={handleAgentChange}
            name="agent"
            className="selectinput"
            value={selectedAgent}
          />
        </div>
      </div>

      <div className="agents-charts">
        {filteredAgents.length === 0 ? (
          <p>No data available</p>
        ) : (
          filteredAgents.map((agent, idx) => (
            <div key={idx} className="agent-chart bg-white">
              <HighchartsReact
                highcharts={Highcharts}
                options={getChartOptions(agent.chartData, agent.percent, agent.agent)}
              />
              <div className="text-center mt-2 font-semibold">{agent.agent}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
